import {Button} from "@components/ui";
import {Header, Layout} from '@components/common'
import {fetchJson, getShopNavigation, redirectTo404Page} from "../index";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import Image from "next/image";
import {getStrapiMedia} from "@utils/strapi";
import delve from "dlv";
import Link from "next/link";
import s from "./riders.module.scss";
import {useTranslation} from "next-i18next";

export interface RiderId {
  id: number;
  name: string;
}

export interface CurrentTeam {
  id: number;
  season: number;
  team: string;
}

export interface Perc {
  onedayraces: number;
  gc: number;
  itt: number;
  sprint: number;
}

export interface PntsBySpecialty {
  perc: Perc;
}

export interface Team {
  season: number;
  team: string;
  abbrev: string;
  classification: string;
  nation: string;
  rider_uci_rnk: number;
  rider_pcs_rnk: number;
}

export interface Race {
  event_id: number;
  id: number;
  date: string;
  rnk: number;
  scraped: number;
  name: string;
  city_start: string;
  city_finish: string;
  race_type: number;
  distance: number;
  pcs_pnts: number;
  uci_pnts: number;
  stage_nr: any;
}

export interface Result {
  event_id: number;
  gc_id: number;
  name: string;
  nation: string;
  classification: string;
  date_start: string;
  date_end: string;
  is_stagerace: number;
  races: Race[];
}

export interface Race2 {
  position: number;
  race: string;
  subtitle: string;
  city_start: string;
  city_finish: string;
  classification: any;
  date: string;
  jaar: number;
  season: number;
  pnt: number;
}

export interface Palmare {
  season: number;
  races: Race2[];
}

export interface PCSData {
  type_api: string;
  rider_ids: RiderId[];
  id: number;
  full_name: string;
  nation: string;
  birthdate: string;
  rider_img: string;
  weight: number;
  height: number;
  current_team: CurrentTeam;
  pnts_by_specialty: PntsBySpecialty;
  teams: Team[];
  results_year: number[];
  selected_year: number;
  results: Result[];
  palmares: Palmare[];
}

export interface Rider {
  Name: string,
  Slug: string,
  ProCyclingID: string,
  Slogan: string,
  Tags: string[],
  Instagram?: string,
  Twitter?: string,
  Image?: any,
  PCSdata?: PCSData
}

export async function getServerSideProps(context: any) {
  try {
    const { locale, params } = context;
    const slug = params.slug[0];
    const proCyclingStatsApiKey = 'c4ba56cc03e6f9fe1019edecdafe8c1168c986db';

    // first get rider from CMS
    const rider: Rider = await fetchJson(
      `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:1337"}/api/riders?filters[slug][$eq]=${slug}&populate=*`,
      'data.0.attributes'
    );

    if (!rider || !rider.ProCyclingID) {
      return redirectTo404Page();
    }

    // use rider Pro Cycling Stats (PCS) ID to get details from PCS
    // We created a full TS type for the PCS data above using their JSON
    // response and putting it into http://json2ts.com/
    rider.PCSdata = await fetchJson(
      `https://www.procyclingstats.com/dataservice.php?key=${proCyclingStatsApiKey}&code=api-boh-rider&id=${rider.ProCyclingID}`,
      null
    );

    if (!rider.PCSdata) {
      return redirectTo404Page();
    }

    return {
      props: {
        ...(await serverSideTranslations(locale, ["common", "home"])),
        ...(await getShopNavigation(locale)), // makes "ShopNavigationColumn" property available on page props
        rider,
        locale
      }
    };
  } catch (error) {
    console.error(error)
    return redirectTo404Page();
  }
}

function ordinal_suffix_of(i: number) {
  var j = i % 10,
    k = i % 100;
  if (j == 1 && k != 11) {
    return i + "st";
  }
  if (j == 2 && k != 12) {
    return i + "nd";
  }
  if (j == 3 && k != 13) {
    return i + "rd";
  }
  return i + "th";
}

export default function Rider(
  data: { rider: Rider }
) {
  const { t } = useTranslation("common");
  const rider: Rider = data.rider;
  return (
    <div className="container-rg">
      <Header></Header>
      <div className={s.riderDetail}>
      <header className={s.titleBar}>
        <div>
          <h1>{rider.PCSdata?.full_name}</h1>
          {
            rider.Slogan ??
              <p>{rider.Slogan}</p>
          }
          </div>
          <Button href={`../riders/`} Component={'a'} variant="ghost">{t("all-riders")}</Button>
      </header>

      <main>
        <div className={s.portrait}>
          <Image
            className="object-contain"
            src={getStrapiMedia(delve(rider.Image, 'data.attributes.url')) || ""}
            alt={delve(rider.Image, 'data.attributes.alternativeText') || ""}
            width={600}
            height={600}
            priority={false}
            quality="85"
          />
        </div>
        <div className={s.facts}>
          <h4>Hardfacts</h4>
          <table>
            <tbody>
            <tr><td width="35%">{t("rider-birthday")}</td><td>{rider.PCSdata?.birthdate}</td></tr>
            <tr><td width="35%">{t("rider-height")}</td><td>{rider.PCSdata?.height} cm</td></tr>
            <tr><td width="35%">{t("rider-weight")}</td><td>{rider.PCSdata?.weight} kg</td></tr>
            <tr><td width="35%">{t("rider-specialty")}</td><td> {
              rider.Tags?.length > 0 ?
                rider.Tags.map((tag: string, index: number) => {
                  return (<span key={index} className={s.tags}>
                    {index !== 0 ? <> - </> : null}{tag}
                  </span>);
                }) : null
            }</td></tr>
            </tbody>
          </table>
          <h4>{t("teams")}</h4>
          <table>
            <tbody>
            {
              rider.PCSdata?.teams ?
                rider.PCSdata?.teams.map((team, index: number) => {
                  return (
                    <tr key={index}><td width="35%">{team.season}</td><td>{team.team}</td></tr>
                  )
                }) : null
            }
            </tbody>
          </table>
        </div>

      </main>

        { rider.PCSdata?.palmares ?
          <div className={s.palmares}>
            <div className={s.container}>
              <h4>Palmarés</h4>
              <div className={s.palmaresContent}>
                {
                  rider.PCSdata?.palmares?.map(palmare => {
                    return (
                      <>
                        <div className={s.entry}>
                          <h5>{palmare.season}</h5>
                          <div>
                            {
                              palmare?.races?.map((race => {

                                return (
                                  <>
                                    <p
                                      className="mb-1">{ordinal_suffix_of(race.position)} {race.race}{race.classification ? ', ' + race.classification : ''}</p>
                                  </>
                                )
                              }))
                            }
                          </div>
                        </div>
                      </>
                    )
                  })
                }</div>
            </div>
            <div className="md:pl-10 text-center md:text-left pt-4 md:pt-0">
              <Link legacyBehavior href={`https://example.com`} aria-label="Open Pro Cycling stats in new window">
                <a className="text-brand" rel="noreferrer" target="_blank" href={'https://www.procyclingstats.com/rider.php?c=1&id=' + rider.ProCyclingID}>{t("rider-visit-profile")} ›</a>
              </Link>
            </div>
          </div> : null
        }
      </div>
    </div>
  )
}

Rider.Layout = Layout
